/**
 * The engine's SIP boundary. Deliberately has no way to originate a call: there is no INVITE,
 * dial or URI-target method. The browser endpoint only registers and answers or declines
 * server-originated legs; calls start through `POST /api/v1/calls`.
 */
import type { IceServerConfig } from './bootstrap.js';
import type { MediaStreamLike, MediaStreamTrackLike } from './platform.js';
import type { Disposer } from './result.js';
import type { SipSocketObservation } from './transport-diagnostics.js';

type SignalingEndpoint = {readonly websocketUrl:string|undefined;readonly httpSignalingUrl?:never} | {readonly httpSignalingUrl:string;readonly websocketUrl?:never};
export type SipTransportConfig = SignalingEndpoint & {

  readonly aor: string;
  readonly username: string;
  readonly password: string;
  readonly registerExpiresSeconds: number;
  readonly iceServers: readonly IceServerConfig[];
  readonly iceTransportPolicy: 'all' | 'relay';
  readonly userAgentString: string;
  /** Seconds SIP.js waits before auto-rejecting an unanswered offer; the PBX ring timeout should fire first. */
  readonly noAnswerTimeoutSeconds: number;
}

export type SipFailure = { readonly code: string; readonly statusCode?: number };

export interface SipTransportHandlers {
  onTransportState(state: 'connected' | 'disconnected', error?: SipFailure): void;
  /** Unsolicited registration changes, for example the registration expiring without a refresh. */
  onRegistrationLost(error?: SipFailure): void;
  onOffer(offer: SipSession): void;
  /**
   * Optional: sanitized WebSocket observations (socket created/open/error/closed with code, reason and
   * wasClean, connect timeout, connect-src policy violations). Codes, flags and numbers only.
   */
  onTransportObservation?(observation: SipSocketObservation): void;
}

export interface SipTransport {
  readonly connected: boolean;
  connect(): Promise<void>;
  /** Reconnect the same user agent so established dialogs survive a WebSocket drop. */
  reconnect(): Promise<void>;
  /** Resolves on a 2xx REGISTER; rejects with `SipFailure` otherwise. Digest challenges are handled internally. */
  register(): Promise<void>;
  unregister(): Promise<void>;
  /** Rotate the digest credentials used for later REGISTER challenges. */
  updateCredentials(credentials: { readonly username: string; readonly password: string }): void;
  /** Stop the user agent. Any remaining dialogs are terminated. */
  disconnect(): Promise<void>;
}

export type SipSessionState = 'offered' | 'accepting' | 'established' | 'terminated';

export type SipTerminationReason =
  | 'remote_cancel'
  | 'remote_bye'
  | 'local_reject'
  | 'local_bye'
  | 'answer_failed'
  | 'transport_stopped';

export type SipMediaState = 'new' | 'connecting' | 'connected' | 'disconnected' | 'failed' | 'closed';

/** One server-originated INVITE dialog. */
export interface SipSession {
  /** Local dialog identity; not an application ID. */
  readonly id: string;
  readonly state: SipSessionState;
  header(name: string): string | undefined;
  /** 180 Ringing. Sent only after the engine has correlated and authorized the offer. */
  progress(): Promise<void>;
  /** Answer with a microphone stream the engine already acquired. Rejects if the dialog ends first. */
  accept(local: MediaStreamLike): Promise<void>;
  reject(statusCode: number): Promise<void>;
  bye(): Promise<void>;
  onTerminated(callback: (reason: SipTerminationReason) => void): Disposer;
  remoteStream(): MediaStreamLike | null;
  onRemoteStream(callback: (stream: MediaStreamLike) => void): Disposer;
  onMediaState(callback: (state: SipMediaState) => void): Disposer;
  replaceLocalTrack(track: MediaStreamTrackLike): Promise<void>;
  /** RFC 4733 via RTCDTMFSender. Optional path; the default DTMF path is the server action. */
  sendRtpDtmf(digits: string): boolean;
  /** In-dialog re-INVITE with ICE restart. Not verified against Asterisk; off by default. */
  restartIce(): Promise<void>;
  /** Raw RTCStatsReport entries of the live peer connection, or null. The engine reduces them to the diagnostics allowlist. */
  diagnosticStats?(): Promise<Iterable<Readonly<Record<string, unknown>>> | null>;
}

export interface SipTransportFactory {
  create(config: SipTransportConfig, handlers: SipTransportHandlers): SipTransport;
}

export class SipError extends Error {
  constructor(readonly failure: SipFailure) {
    super(failure.code);
    this.name = 'SipError';
  }
}

export function sipFailureOf(error: unknown): SipFailure {
  if (error instanceof SipError) return error.failure;
  return { code: 'sip_error' };
}
